import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Main } from '@/store/enums/MainEnums';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { AxiosRequestConfig } from 'axios';
import { StoreModel } from '@/assets/ts/_utils/models/StoreModel';
import { domainUrl } from '@/core/config/DomainConfig';
import { serializedPropsElement } from '@/assets/ts/_utils';
import { ErrorGetters } from '@/store/enums/ErrorGettersEnums';
import { Getters } from '@/store/enums/GettersEnums';

export interface App {
    id: string,
    uuid: string,
    title: string,
    store: number,
    icon: string,
    package?: string,
    developer?: { name?: string },
    appstore?: { tag?: string },
    is_favorite?: boolean,
}

export interface CreateApp {
    selectCountry: string | unknown,
    appstore: number | unknown,
    selectApplication: string
}

export interface AppInfo {
    errors: unknown;
    app: App;
    apps: App[];
    search_apps: unknown;
}

const setFullApps = (newApps: any, loadedApps: any) => {
  const output: App[] = [];

  for (let i = 0; i < newApps.length; i++) {
    let isAppNew = true;
    for (let j = 0; j < loadedApps.length; j++) {
      if (newApps[i].uuid === loadedApps[j].uuid) {
        isAppNew = false;
        break;
      }
    }
    if (isAppNew) {
      output.push(newApps[i]);
    }
  }

  return output;
};

const getToggledAppIndex = (apps, id) => {
  let appIndex: null | number = null;

  for (let i = 0; i < apps.length; i++) {
    if (apps[i].app.package === id) {
      appIndex = i;
      break;
    }
  }
  return appIndex;
};

@Module({ name: 'AppModule' })
export default class AppModule extends VuexModule implements AppInfo, StoreModel {
  errors = {};
  app = {} as App;
  appstore = Main.STORE_GOOGLE_MARKET;
  appman_app = {} as App;
  apps = [] as App[];
  total = 0;
  count = 0;
  search_apps = [];
  isLoading = false;
  lastAddedApp = {} as App;
  selectApps: App[] = [];

  fullAppsCount = {};
  fullApps: App[] = [];
  fullAppsPage = 1;
  fullAppsLimit = 10;
  fullAppsLastPage = null;

  fullAppsAndroid: App[] = [];
  fullAppsIos: App[] = [];
  fullAppsBanned: App[] = [];
  fullAppsFavorite: App[] = [];

  get [Getters.GET_LAST_ADDED_APP](): App {
    return this.lastAddedApp;
  }

  /**
     * Get count
     * @returns number
     */
  get [Getters.GET_APPS_COUNT](): number {
    return this.count;
  }

  /**
     * Get current app object
     * @returns App
     */
  get [Getters.CURRENT_APP](): App {
    return this.app;
  }

  /**
     * Get current appstore
     * @returns
     */
  get [Getters.CURRENT_APPSTORE]() {
    return this.appstore;
  }

  /**
     * Get current Appman app object
     * @returns Appman
     */
  get [Getters.CURRENT_APPMAN_APP](): App {
    return this.appman_app;
  }

  /**
     * Get  errors
     * @returns array
     */
  get [ErrorGetters.GET_ERRORS_APP]() {
    return this.errors;
  }

  /**
     * Get current Apps objects
     * @returns App
     */
  get [Getters.ALL_APPS]() {
    return this.apps;
  }

  get [Getters.ALL_SELECT_APPS]() {
    return this.selectApps;
  }

  get [Getters.ALL_FULL_APPS_COUNT]() {
    return this.fullAppsCount;
  }

  get [Getters.ALL_FULL_APPS]() {
    return this.fullApps;
  }

  get [Getters.ALL_FULL_APPS_ANDROID]() {
    return this.fullAppsAndroid;
  }

  get [Getters.ALL_FULL_APPS_IOS]() {
    return this.fullAppsIos;
  }

  get [Getters.ALL_FULL_APPS_BANNED]() {
    return this.fullAppsBanned;
  }

  get [Getters.ALL_FULL_APPS_FAVORITE]() {
    return this.fullAppsFavorite;
  }

  get [Getters.ALL_FULL_APPS_LAST_PAGE](): number | null {
    return this.fullAppsLastPage;
  }

  get [Getters.ALL_FULL_APPS_PAGE]() {
    return this.fullAppsPage;
  }


  /**
     * Get current Countries objects
     * @returns App
     */
  get [Getters.ALL_SEARCH_APPS]() {
    return this.search_apps;
  }

  /**
     * Get current total count
     * @returns Apps
     */
  get [Getters.CURRENT_TOTAL]() {
    return this.total;
  }

    @Mutation
  [Mutations.CLEAR_STORE]() {
    this.errors = {};
    this.app = {} as App;
    this.appstore = Main.STORE_GOOGLE_MARKET;
    this.appman_app = {} as App;
    this.apps = [];
    this.total = 0;
    this.count = 0;
    this.search_apps = [];
    this.selectApps = [];
    this.isLoading = false;
    this.fullApps = [];
    this.fullAppsCount = {};
    this.fullAppsPage = 1;
    this.fullAppsLimit = 12;
    this.fullAppsLastPage = null;
    this.fullAppsAndroid = [];
    this.fullAppsIos = [];
    this.fullAppsBanned = [];
    this.fullAppsFavorite = [];
  };

    @Mutation
    [Mutations.SET_APPS_COUNT](payload) {
      this.count = payload;
    }

    @Mutation
    [Mutations.SET_LAST_ADDED_APP](payload) {
      this.lastAddedApp = payload;
    }

    @Mutation
    [Mutations.SET_LOADING](isLoading) {
      this.isLoading = isLoading;
    }

    @Mutation
    [Mutations.SET_TOTAL_PAGES](total) {
      this.total = total;
    }

    @Mutation
    [Mutations.SET_ERROR_APP](error) {
      this.errors = { ...error };
    }

    @Mutation
    [Mutations.SET_APPS](apps) {
      this.apps = apps;
    }

    @Mutation
    [Mutations.SET_SELECT_APPS](apps) {
      this.selectApps = apps;
    }

    @Mutation
    [Mutations.RESET_FULL_APPS]() {
      this.fullApps = [];
    }

    @Mutation
    [Mutations.RESET_FULL_APPS_ANDROID]() {
      this.fullAppsAndroid = [];
    }

    @Mutation
    [Mutations.RESET_FULL_APPS_IOS]() {
      this.fullAppsIos = [];
    }

    @Mutation
    [Mutations.RESET_FULL_APPS_FAVORITE]() {
      this.fullAppsFavorite = [];
    }

    @Mutation
    [Mutations.RESET_FULL_APPS_BANNED]() {
      this.fullAppsBanned = [];
    }

    @Mutation
    [Mutations.TOGGLE_FAVOTIRE_APP_BY_ID](id) {
      const appIndexAll = getToggledAppIndex(this.fullApps, id);
      const appIndexAndroid = getToggledAppIndex(this.fullAppsAndroid, id);
      const appIndexIos = getToggledAppIndex(this.fullAppsIos, id);
      const appIndexFavorite = getToggledAppIndex(this.fullAppsFavorite, id);
      const appIndexBanned = getToggledAppIndex(this.fullAppsBanned, id);

      if (appIndexAll !== null && this.fullApps[appIndexAll]?.is_favorite !== undefined) {
        this.fullApps[appIndexAll].is_favorite = !this.fullApps[appIndexAll].is_favorite;
      }

      if (appIndexAndroid !== null && this.fullAppsAndroid[appIndexAndroid]?.is_favorite !== undefined) {
        this.fullAppsAndroid[appIndexAndroid].is_favorite = !this.fullAppsAndroid[appIndexAndroid].is_favorite;
      }

      if (appIndexIos !== null && this.fullAppsIos[appIndexIos]?.is_favorite !== undefined) {
        this.fullAppsIos[appIndexIos].is_favorite = !this.fullAppsIos[appIndexIos].is_favorite;
      }

      if (appIndexBanned !== null && this.fullAppsBanned[appIndexBanned]?.is_favorite !== undefined) {
        this.fullAppsBanned[appIndexBanned].is_favorite = !this.fullAppsBanned[appIndexBanned].is_favorite;
      }

      if (appIndexFavorite !== null && this.fullAppsFavorite[appIndexFavorite]?.is_favorite !== undefined) {
        this.fullAppsFavorite[appIndexFavorite].is_favorite = !this.fullAppsFavorite[appIndexFavorite].is_favorite;
      }
    }

    @Mutation
    [Mutations.SET_FULL_APPS_ANDROID](apps) {
      const newApps = setFullApps(apps, this.fullAppsAndroid);
      this.fullAppsAndroid = [...this.fullAppsAndroid, ...newApps];
    }

    @Mutation
    [Mutations.SET_FULL_APPS_IOS](apps) {
      const newApps = setFullApps(apps, this.fullAppsIos);
      this.fullAppsIos = [...this.fullAppsIos, ...newApps];
    }

    @Mutation
    [Mutations.SET_FULL_APPS_BANNED](apps) {
      const newApps = setFullApps(apps, this.fullAppsBanned);
      this.fullAppsBanned = [...this.fullAppsBanned, ...newApps];
    }

    @Mutation
    [Mutations.SET_FULL_APPS_FAVORITE](apps) {
      const newApps = setFullApps(apps, this.fullAppsFavorite);
      this.fullAppsFavorite = [...this.fullAppsFavorite, ...newApps];
    }

    @Mutation
    [Mutations.SET_FULL_APPS](apps: App[]) {
      const newApps = setFullApps(apps, this.fullApps);
      this.fullApps = [...this.fullApps, ...newApps];
    }

    @Mutation
    [Mutations.SET_FULL_APPS_PAGE](page: number) {
      this.fullAppsPage = page;
    }

    @Mutation
    [Mutations.SET_FULL_APPS_COUNT](count) {
      this.fullAppsCount = count;
    }

    @Mutation
    [Mutations.SET_FULL_APPS_LAST_PAGE](last_page) {
      this.fullAppsLastPage = last_page;
    }

    @Mutation
    [Mutations.SET_SEARCH_APPS](search_apps) {
      this.search_apps = search_apps;
    }

    @Mutation
    [Mutations.SET_APPMAN_APP](appman_app) {
      this.appman_app = appman_app;
    }

    @Mutation
    [Mutations.SET_APP](app) {
      this.app = app;
    }

    @Mutation
    [Mutations.SET_APPSTORE](appstore) {
      this.appstore = appstore;
    }

    @Action
    [Actions.GET_APPMAN_APP](params) {
      const config: AxiosRequestConfig = {
        method: 'GET',
        params: params
      };
      return ApiService.query(`${domainUrl}apps/show-by-package`, config)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_APPMAN_APP, data.data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR_APP, response?.data?.errors);
          this.context.commit(Mutations.SET_APPMAN_APP, {});
        });
    }

    @Action
    [Actions.GET_SEARCH_APPS](params) {
      const config: AxiosRequestConfig = {
        method: 'GET',
        params: params
      };
      return ApiService.query(`${domainUrl}apps/search`, config)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_SEARCH_APPS, data.data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR_APP, response?.data?.errors);
        });
    }

    @Action
    [Actions.GET_SELECT_APPS](params) {
      const config: AxiosRequestConfig = {
        method: 'GET',
        params: params
      };
      return ApiService.query(`${domainUrl}apps`, config)
        .then(({ data }) => {
          const serializedData = data.data.map(el => ({ ...el, app: { ...serializedPropsElement(el.app, 'icon', el?.country?.id) } }));
          this.context.commit(Mutations.SET_SELECT_APPS, serializedData);
        })
        .catch(({ response }) => {
          // this.context.commit(Mutations.SET_ERROR_APP, response?.data?.errors);
        });
    }

    @Action
    [Actions.GET_APPS](params) {
      this.context.commit(Mutations.SET_LOADING, true);
      const config: AxiosRequestConfig = {
        method: 'GET',
        params: params
      };
      return ApiService.query(`${domainUrl}apps`, config)
        .then(({ data }) => {
          const serializedData = data.data.map(el => ({ ...el, app: { ...serializedPropsElement(el.app, 'icon', el?.country?.id) } }));
          this.context.commit(Mutations.SET_TOTAL_PAGES, serializedData?.length);
          this.context.commit(Mutations.SET_APPS, serializedData);
          this.context.commit(Mutations.SET_APPS_COUNT, data?.count);

          if (params?.full_data && params?.counts) {
            this.context.commit(Mutations.SET_FULL_APPS_COUNT, data.count);
            this.context.commit(Mutations.SET_FULL_APPS_LAST_PAGE, data?.meta?.last_page);
            switch (params.type) {
              case Main.ALL:
                this.context.commit(Mutations.SET_FULL_APPS, serializedData);
                break;
              case Main.ANDROID:
                this.context.commit(Mutations.SET_FULL_APPS_ANDROID, serializedData);
                break;
              case Main.IOS:
                this.context.commit(Mutations.SET_FULL_APPS_IOS, serializedData);
                break;
              case Main.FAV:
                this.context.commit(Mutations.SET_FULL_APPS_FAVORITE, serializedData);
                break;
              case Main.BAN:
                this.context.commit(Mutations.SET_FULL_APPS_BANNED, serializedData);
                break;
              default:
                this.context.commit(Mutations.SET_FULL_APPS, serializedData);
                break;
            }
          }
        })
        .catch(({ response }) => {
          // this.context.commit(Mutations.SET_ERROR_APP, response?.data?.errors);
        }).finally(() => {
          this.context.commit(Mutations.SET_LOADING, false);
        });
    }

    @Action
    [Actions.DELETE_APP](id) {
      return ApiService.delete(`${domainUrl}apps/` + id)
        .then(({ data }) => {
          const deleteApps = this.apps.filter((obj) => {
            // @ts-ignore
            if (obj.app.package !== id) {
              return obj;
            }
          });
          this.context.commit(Mutations.SET_APPS, deleteApps);
          this.context.commit(Mutations.SET_TOTAL_PAGES, this.apps.length);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR_APP, response?.data?.errors);
        }).finally(() => {
          this.context.commit(Mutations.SET_LOADING, false);
        });
    }

    @Action
    [Actions.DELETE_BANNED_APPS](id) {
      return ApiService.delete(`${domainUrl}apps/destroyBanned`)
        .then(({ data }) => {
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR_APP, response?.data?.errors);
        }).finally(() => {
          this.context.commit(Mutations.SET_LOADING, false);
        });
    }

    @Action
    [Actions.UPDATE_APP](id) {
      return ApiService.update(`${domainUrl}apps`, id, {})
        .then(({ data }) => {
          const el = data.data;
          const serializedData = { ...el, app: { ...serializedPropsElement(el.app) } };
          this.context.commit(Mutations.SET_APP, serializedData);
          this.context.commit(Mutations.SET_ERROR_APP, {});
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR_APP, response?.data?.errors);
        }).finally(() => {
          this.context.commit(Mutations.SET_LOADING, false);
        });
    }

    @Action
    [Actions.TOGGLE_FAVORITE_APP](id: any) {
      const config: AxiosRequestConfig = {
        params: { id: id }
      };
      return ApiService.put(`${domainUrl}apps/toggle`, config)
        .then(({ data }) => {
          const serializedData = data.data.map(el => ({ ...el, app: { ...serializedPropsElement(el.app, 'icon') } }));
          this.context.commit(Mutations.SET_APP, serializedData);
          const updateApps = this.apps.map((obj) => {
            // @ts-ignore
            if (obj.app.package === data.data.app.package) {
              return data.data;
            }
            return obj;
          });
          this.context.commit(Mutations.SET_APPS, updateApps);
          this.context.commit(Mutations.SET_ERROR_APP, {});
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR_APP, response?.data?.errors);
        }).finally(() => {
          this.context.commit(Mutations.SET_LOADING, false);
        });
    }

    @Action
    [Actions.ADD_APP](params) {
      return ApiService.post(`${domainUrl}apps`, params)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_LAST_ADDED_APP, data.data);
          const serializedData = data.data.map(el => ({ ...el, app: { ...serializedPropsElement(el.app, 'icon') } }));
          this.context.commit(Mutations.SET_APP, serializedData);
          if (!this.apps.some(el => el?.uuid === data.data?.uuid)) {
            this.context.commit(Mutations.SET_APPS, [data.data, ...this.apps]);
          }
          this.context.commit(Mutations.SET_ERROR_APP, {});
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR_APP, response?.data?.errors);
        });
    }

    @Action
    [Actions.GET_APP](id) {
      return ApiService.get(`${domainUrl}apps`, id)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_APP, { app: data.data });
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR_APP, response?.data?.errors);
        });
    }


}
